import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import rankingData from "@/data/ranking_pages.json";
import { listMetaDescription, listTitle } from "@/lib/ranking-display-names";

type RankingPage = (typeof rankingData.pages)[number];

const pagesBySlug = new Map<string, RankingPage>(rankingData.pages.map((p) => [p.slug, p]));

export const Route = createFileRoute("/lists/$slug")({
  loader: ({ params }) => {
    const page = pagesBySlug.get(params.slug);
    if (!page) throw notFound();
    return {
      page,
      methodology: rankingData.methodology,
      bridge: rankingData.bridge,
    };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Not found | CapitalDart" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://capitaldart.com/lists/${params.slug}`;
    const heading = listTitle(loaderData.page.stage, loaderData.page.category);
    const title = `${heading} | CapitalDart`;
    const desc = listMetaDescription(loaderData.page.stage, loaderData.page.category);
    const image = "https://capitaldart.com/capitaldart-og.png";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: heading,
            description: desc,
            url,
            itemListElement: loaderData.page.rows.map((row) => ({
              "@type": "ListItem",
              position: row.rank,
              name: row.name,
            })),
          }),
        },
      ],
    };
  },
  component: RankingListPage,
});

function RankingListPage() {
  const { page, bridge } = Route.useLoaderData();

  return (
    <main className="bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
          {listTitle(page.stage, page.category)}
        </h1>

        <div className="mt-10 overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-left text-sm md:text-base">
            <thead>
              <tr className="border-b border-border bg-card">
                <th className="px-4 py-3 font-semibold w-20">Rank</th>
                <th className="px-4 py-3 font-semibold">Investor Name</th>
              </tr>
            </thead>
            <tbody>
              {page.rows.map((row) => (
                <tr key={row.rank} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3 tabular-nums">{row.rank}</td>
                  <td className="px-4 py-3">{row.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-10 text-base leading-relaxed md:text-lg">{page.commentary}</p>

        <p className="mt-8 text-base leading-relaxed">{bridge}</p>

        <p className="mt-4 text-base leading-relaxed">
          Create a free account and get the contacts for the investors above.
        </p>

        <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
          Rankings cover January 2025 to July 2026 investment activity in US and Canada companies.
          Seed and early-stage rankings measure deal participation, not suitability for every raise.
        </p>

        <div className="mt-8">
          <Link
            to="/auth"
            search={{
              mode: "signup" as const,
              ranking_category: page.category,
              ranking_stage: page.stage,
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Get the contacts for these investors, free
          </Link>
        </div>
      </div>
    </main>
  );
}
